<template>
<div class="bt-page">
  <div class="bt-header">
    <div class="bt-header-inner">
      <div class="bt-logo">simitrac</div>
      <div class="bt-subtitle">Live Bus Tracking</div>
    </div>
  </div>

  <div v-if="loading" class="bt-loading">
    <div class="spinner-border" style="color:#2E86DE;"></div>
    <div style="margin-top:12px;color:#888;">Loading bus location...</div>
  </div>

  <div v-else-if="error" class="bt-error">
    <div style="font-size:48px;">🚌</div>
    <h4>{{ error }}</h4>
    <p>Unable to load bus tracking. The link may be invalid.</p>
  </div>

  <div v-else-if="data" class="bt-content">
    <!-- Bus Info Card -->
    <div class="bt-card bt-bus-card">
      <div class="bt-bus-icon">🚌</div>
      <div class="bt-bus-info">
        <div class="bt-bus-plate">{{ data.bus.registration_number || data.bus.name }}</div>
        <div class="bt-bus-route" v-if="data.bus.route">{{ data.bus.route }}</div>
      </div>
      <div class="bt-live-badge" :class="data.bus.is_live ? 'bt-live' : 'bt-offline'">
        <span class="bt-live-dot"></span>
        {{ data.bus.is_live ? 'LIVE' : 'OFFLINE' }}
      </div>
    </div>

    <!-- Map -->
    <div class="bt-card bt-map-card">
      <div id="busTrackMap" class="bt-map"></div>
      <div class="bt-map-info">
        <span v-if="data.bus.speed > 0"><i class="mdi mdi-speedometer"></i> {{ data.bus.speed }} km/h</span>
        <span v-if="data.bus.last_update">Updated: {{ formatTime(data.bus.last_update) }}</span>
        <span v-if="autoRefresh" style="color:#10B981;">● Auto-refreshing</span>
      </div>
    </div>

    <!-- Driver Info -->
    <div class="bt-card" v-if="data.bus.driver_name">
      <div class="bt-card-title">🧑‍✈️ Driver</div>
      <div class="bt-driver">
        <div>
          <div class="bt-driver-name">{{ data.bus.driver_name }}</div>
          <div class="bt-driver-phone" v-if="data.bus.driver_phone">
            <a :href="'tel:' + data.bus.driver_phone">{{ data.bus.driver_phone }}</a>
          </div>
        </div>
        <a v-if="data.bus.driver_phone" :href="'tel:' + data.bus.driver_phone" class="bt-call-btn">📞 Call</a>
      </div>
    </div>

    <!-- Students on Bus -->
    <div class="bt-card" v-if="data.students_on_bus && data.students_on_bus.length">
      <div class="bt-card-title">👨‍🎓 Students on Bus ({{ data.students_on_bus.length }})</div>
      <div class="bt-student" v-for="s in data.students_on_bus" :key="s.admission_no">
        <span class="bt-student-name">{{ s.name }}</span>
        <span class="bt-student-adm">{{ s.admission_no }}</span>
      </div>
    </div>

    <!-- Footer -->
    <div class="bt-footer">
      <div>© 2026 Simitrac — Save Smart, Spend Smart</div>
      <a href="https://simitrac.co.ke">simitrac.co.ke</a>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import { executeJson, simitrack } from "@/api";
export default {
  name: "BusTracker",
  data() {
    return { loading: true, error: '', data: null, map: null, busMarker: null, trailLine: null, autoRefresh: true, refreshTimer: null };
  },
  mounted() { this.loadLeaflet(); this.loadBus(); },
  beforeUnmount() { if (this.refreshTimer) clearInterval(this.refreshTimer); },
  methods: {
    loadLeaflet() {
      if (!document.getElementById('leaflet-css-bt')) {
        var link = document.createElement('link'); link.id = 'leaflet-css-bt'; link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
      }
      if (!window.L) {
        var script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; document.head.appendChild(script);
      }
    },
    async loadBus() {
      var busId = this.$route.params.busId;
      var token = this.$route.query.t || '';
      if (!busId) { this.error = 'Invalid link'; this.loading = false; return; }
      try {
        var r = await executeJson({ function: 'getBusLocation', busId: busId, token: token }, simitrack);
        if (r.data.success) {
          this.data = r.data.data;
          this.$nextTick(() => { setTimeout(() => this.initMap(), 500); });
          // Auto-refresh every 15 seconds if bus is live
          if (this.data.bus.is_live) {
            this.refreshTimer = setInterval(() => this.refreshLocation(), 15000);
          }
        } else {
          this.error = r.data.message || 'Bus not found';
        }
      } catch (e) { this.error = 'Connection error'; }
      this.loading = false;
    },
    async refreshLocation() {
      var busId = this.$route.params.busId;
      var token = this.$route.query.t || '';
      try {
        var r = await executeJson({ function: 'getBusLocation', busId: busId, token: token }, simitrack);
        if (r.data.success) {
          this.data = r.data.data;
          this.updateMapPosition();
        }
      } catch (e) {}
    },
    initMap() {
      if (!window.L || !this.data || !this.data.bus.gps_lat) return;
      var lat = parseFloat(this.data.bus.gps_lat);
      var lng = parseFloat(this.data.bus.gps_lng);
      this.map = L.map('busTrackMap').setView([lat, lng], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap', maxZoom: 19
      }).addTo(this.map);

      // Bus marker with custom icon
      var busIcon = L.divIcon({
        className: 'bt-bus-marker',
        html: '<div style="width:36px;height:36px;background:#F59E0B;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-size:18px;">🚌</div>',
        iconSize: [36, 36], iconAnchor: [18, 18]
      });
      this.busMarker = L.marker([lat, lng], { icon: busIcon }).addTo(this.map);
      this.busMarker.bindPopup('<b>' + (this.data.bus.registration_number || this.data.bus.name) + '</b><br>' + (this.data.bus.route || ''));

      // Draw trail
      if (this.data.trail && this.data.trail.length > 1) {
        var trailCoords = this.data.trail.map(p => [parseFloat(p.gps_lat), parseFloat(p.gps_lng)]);
        this.trailLine = L.polyline(trailCoords, { color: '#2E86DE', weight: 4, opacity: 0.7, dashArray: '8, 8' }).addTo(this.map);
        // Add start and end markers
        var startIcon = L.divIcon({ className: '', html: '<div style="width:10px;height:10px;background:#10B981;border-radius:50%;border:2px solid #fff;"></div>', iconSize: [10, 10], iconAnchor: [5, 5] });
        L.marker(trailCoords[0], { icon: startIcon }).addTo(this.map).bindPopup('Route start');
      }
    },
    updateMapPosition() {
      if (!this.map || !this.busMarker || !this.data.bus.gps_lat) return;
      var lat = parseFloat(this.data.bus.gps_lat);
      var lng = parseFloat(this.data.bus.gps_lng);
      this.busMarker.setLatLng([lat, lng]);
      this.map.panTo([lat, lng]);
      // Update trail
      if (this.data.trail && this.data.trail.length > 1 && this.trailLine) {
        var trailCoords = this.data.trail.map(p => [parseFloat(p.gps_lat), parseFloat(p.gps_lng)]);
        this.trailLine.setLatLngs(trailCoords);
      }
    },
    formatTime(dt) {
      if (!dt) return '-';
      return new Date(dt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    }
  }
};
</script>

<style scoped>
.bt-page{min-height:100vh;background:#F0F2F5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
.bt-header{background:linear-gradient(135deg,#0D2B5E,#1A4B8C);padding:0;}
.bt-header-inner{max-width:480px;margin:0 auto;padding:16px;text-align:center;}
.bt-logo{color:#fff;font-size:20px;font-weight:800;}
.bt-subtitle{color:rgba(255,255,255,.7);font-size:12px;}
.bt-loading,.bt-error{text-align:center;padding:60px 20px;max-width:400px;margin:0 auto;}
.bt-error h4{color:#333;font-size:18px;}.bt-error p{color:#666;font-size:14px;}
.bt-content{max-width:480px;margin:0 auto;padding:12px;}
.bt-card{background:#fff;border-radius:14px;padding:16px;margin-bottom:12px;box-shadow:0 1px 3px rgba(0,0,0,.06);}
.bt-card-title{font-size:14px;font-weight:700;color:#333;margin-bottom:12px;}

.bt-bus-card{display:flex;align-items:center;gap:12px;}
.bt-bus-icon{font-size:36px;}
.bt-bus-info{flex:1;}
.bt-bus-plate{font-size:20px;font-weight:800;color:#1a1a1a;letter-spacing:1px;}
.bt-bus-route{font-size:13px;color:#666;margin-top:2px;}
.bt-live-badge{padding:4px 12px;border-radius:50px;font-size:11px;font-weight:700;display:flex;align-items:center;gap:4px;white-space:nowrap;}
.bt-live{background:#ECFDF5;color:#059669;}
.bt-offline{background:#FEF2F2;color:#DC2626;}
.bt-live-dot{width:8px;height:8px;border-radius:50%;background:currentColor;animation:pulse 1.5s infinite;}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:.3;}}

.bt-map-card{padding:0;overflow:hidden;}
.bt-map{width:100%;height:300px;}
.bt-map-info{display:flex;gap:16px;padding:10px 16px;font-size:12px;color:#888;flex-wrap:wrap;}

.bt-driver{display:flex;justify-content:space-between;align-items:center;}
.bt-driver-name{font-size:15px;font-weight:700;color:#1a1a1a;}
.bt-driver-phone{font-size:13px;color:#2E86DE;margin-top:2px;}
.bt-driver-phone a{color:#2E86DE;text-decoration:none;}
.bt-call-btn{background:#10B981;color:#fff;padding:8px 16px;border-radius:8px;text-decoration:none;font-size:13px;font-weight:700;white-space:nowrap;}

.bt-student{display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid #f5f5f5;}
.bt-student:last-child{border-bottom:none;}
.bt-student-name{font-size:14px;font-weight:600;color:#333;}
.bt-student-adm{font-size:12px;color:#999;}

.bt-footer{text-align:center;padding:20px 16px;color:#aaa;font-size:12px;}
.bt-footer a{color:#2E86DE;text-decoration:none;}
</style>
